import { useTranslation } from "react-i18next";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";

import { userKeys } from "../queryKeys";
import { getUserReviews } from "../services/reviews.service";
import { ReviewsResponse, ReviewType, ReviewTypeEnum } from "../types/reviews.types";

const REVIEWS_PAGE_LIMIT = 5;

export const useUserReviews = (
  userId: string | null,
  limit: number = REVIEWS_PAGE_LIMIT,
  offset: number = 0,
  type: ReviewType = ReviewTypeEnum.RECEIVED
) => {
  const { i18n } = useTranslation();

  return useQuery<ReviewsResponse["data"], Error>({
    queryKey: [...userKeys.reviews(userId!), { limit, offset, type }, i18n.language],
    queryFn: async () => {
      const response = await getUserReviews(userId!, limit, offset, type);
      return response.data;
    },
    enabled: !!userId,
  });
};

export const useInfiniteUserReviews = (userId: string | null, type: ReviewType = ReviewTypeEnum.RECEIVED) => {
  const { i18n } = useTranslation();

  return useInfiniteQuery<
    ReviewsResponse["data"],
    Error,
    ReviewsResponse["data"],
    (string | Record<string, any>)[],
    number
  >({
    queryKey: [...userKeys.reviews(userId!), { type }, "infinite", i18n.language],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getUserReviews(userId!, REVIEWS_PAGE_LIMIT, pageParam, type);
      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.length * REVIEWS_PAGE_LIMIT;
      return totalFetched < lastPage.total ? allPages.length * REVIEWS_PAGE_LIMIT : undefined;
    },
    select: (data) => ({
      items: data.pages.flatMap((page) => page.items),
      total: data.pages[0]?.total ?? 0,
    }),
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};
